import { createQuerySerializer, type Middleware } from 'openapi-fetch'
import type { FlowQuery } from '../types/common'

const payloadByRequest = new WeakMap<Request, string>()

export function getMiddleware(): Middleware {
  return {
    async onRequest({ request }) {
      const method = request.method.toUpperCase()

      const methodContainsBody = method === 'POST' || method === 'PUT'
      if (methodContainsBody) payloadByRequest.set(request, await request.clone().text())

      return request
    },
    async onResponse(data) {
      return await commonOnResponse(data)
    },
  }
}

async function commonOnResponse({ request, response }: { request: Request; response: Response }): Promise<Response> {
  const { ok } = response
  if (ok) return response

  const { method, url } = request
  const { status, statusText } = response

  const error = await response.clone().text()
  const payload = payloadByRequest.get(request)

  const requestError = new Error(`Request: ${method} ${url}, Response: ${status} ${statusText} ${error}`)
  if (payload) Object.assign(requestError, { payload: parsePayload(payload) })

  throw requestError
}

function parsePayload(payload: string): unknown {
  try {
    return JSON.parse(payload)
  } catch {
    return payload
  }
}

const defaultQuerySerializer = createQuerySerializer()

export const flowQuerySerializer = (queryParams: FlowQuery) => {
  const { data, ...rest } = queryParams || {}
  const flattenedData = flattenToDotNotation(data || {}, 'data', {})
  const flattened = { ...rest, ...flattenedData }

  return defaultQuerySerializer(flattened)
}

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

const flattenToDotNotation = (
  value: unknown,
  prefix: string,
  target: Record<string, unknown>,
): Record<string, unknown> => {
  if (isPlainObject(value)) {
    for (const [key, nestedValue] of Object.entries(value)) {
      const nestedPath = `${prefix}.${key}`
      flattenToDotNotation(nestedValue, nestedPath, target)
    }
    return target
  }

  target[prefix] = value
  return target
}
